import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, useMediaQuery, useTheme } from "@mui/material";
import { useLocation } from "react-router-dom";
import apiService from "../services/api";

const STORAGE_KEY = "podscope.reviewTimeTracker.v1";
const WEEK_TOTAL_KEY = "podscope.reviewTimeTracker.weekTotal.v1";
const FLUSH_LOCK_NAME = "podscope.reviewTimeTracker.flush";
const FLUSH_INTERVAL_MS = 15_000;
const MAX_FLUSH_DELTA_MS = 120_000;
const TICK_MS = 1_000;
/** Periodic flushes skip when another tab flushed this recently. */
const MIN_PERIODIC_DELTA_MS = FLUSH_INTERVAL_MS - 2_500;

const DIALOG_OPEN_EVENT = "podscope.reviewTimeTracker.dialogOpen";
const TAB_ID = `tab-${Math.random().toString(36).slice(2, 10)}`;

/** True in this tab while the episode mention review dialog is open. */
let dialogOpenThisTab = false;

/**
 * Tell the /review timer whether the mention review dialog is open in this tab.
 * Tracking runs only while the dialog is open and this tab is visible.
 */
export function setReviewDialogOpen(open: boolean): void {
  dialogOpenThisTab = open;
  window.dispatchEvent(new CustomEvent(DIALOG_OPEN_EVENT, { detail: { open } }));
}

const PODCAST_PATH = /^\/review\/([0-9a-f]{24})(?:\/|$)/i;

type SharedState = {
  running: boolean;
  /** Tab that owns the running session; other /review tabs must not stop or flush it. */
  ownerTabId: string | null;
  sessionStartedAt: number | null;
  lastFlushAt: number | null;
  sessionElapsedMs: number;
  /** Podcast open in the most recently focused /review tab; pending time is credited to it. */
  activePodcastId: string | null;
};

const STOPPED: SharedState = {
  running: false,
  ownerTabId: null,
  sessionStartedAt: null,
  lastFlushAt: null,
  sessionElapsedMs: 0,
  activePodcastId: null,
};

function pageIsActive(): boolean {
  return typeof document !== "undefined" && document.visibilityState === "visible";
}

function shouldTrackReviewTime(dialogOpen: boolean, userPaused: boolean): boolean {
  return dialogOpen && pageIsActive() && !userPaused;
}

function isOwner(state: SharedState): boolean {
  return state.ownerTabId == null || state.ownerTabId === TAB_ID;
}

function podcastIdFromPath(pathname: string): string | null {
  return PODCAST_PATH.exec(pathname)?.[1] ?? null;
}

function loadShared(): SharedState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...STOPPED };
    const parsed = JSON.parse(raw) as Partial<SharedState>;
    return {
      running: Boolean(parsed.running),
      ownerTabId: typeof parsed.ownerTabId === "string" ? parsed.ownerTabId : null,
      sessionStartedAt:
        typeof parsed.sessionStartedAt === "number" ? parsed.sessionStartedAt : null,
      lastFlushAt: typeof parsed.lastFlushAt === "number" ? parsed.lastFlushAt : null,
      sessionElapsedMs:
        typeof parsed.sessionElapsedMs === "number" && parsed.sessionElapsedMs >= 0
          ? parsed.sessionElapsedMs
          : 0,
      activePodcastId:
        typeof parsed.activePodcastId === "string" ? parsed.activePodcastId : null,
    };
  } catch {
    return { ...STOPPED };
  }
}

function saveShared(state: SharedState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore quota / private mode
  }
}

function loadWeekTotalMs(): number | null {
  try {
    const raw = localStorage.getItem(WEEK_TOTAL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { totalMs?: unknown };
    return typeof parsed.totalMs === "number" && parsed.totalMs >= 0
      ? parsed.totalMs
      : null;
  } catch {
    return null;
  }
}

function saveWeekTotalMs(totalMs: number): void {
  try {
    localStorage.setItem(WEEK_TOTAL_KEY, JSON.stringify({ totalMs, updatedAt: Date.now() }));
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Run fn exclusively across tabs via the Web Locks API. Falls back to direct
 * execution where locks are unavailable (races are then rare but possible).
 */
async function runExclusive(fn: () => Promise<void>): Promise<void> {
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (locks?.request) {
    await locks.request(FLUSH_LOCK_NAME, fn);
  } else {
    await fn();
  }
}

function formatSessionClock(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatWeekHours(ms: number): string {
  const hours = ms / 3_600_000;
  if (hours < 10) return `${hours.toFixed(1)}h`;
  return `${Math.round(hours)}h`;
}

function pendingDeltaMs(state: SharedState, at: number): number {
  const since = state.lastFlushAt ?? state.sessionStartedAt ?? at;
  return Math.min(MAX_FLUSH_DELTA_MS, Math.max(0, at - since));
}

function liveSessionMs(state: SharedState, now: number): number {
  if (state.running && state.sessionStartedAt != null) {
    return Math.max(0, now - state.sessionStartedAt);
  }
  return Math.max(0, state.sessionElapsedMs);
}

/**
 * Fixed top-right review timer for /review pages.
 *
 * Tracks only while the mention review dialog is open in this tab and the tab
 * is visible. Closing the dialog, hiding the tab, or leaving /review stops the
 * session and flushes leftover time. Multi-tab safe: the owning tab alone
 * flushes; other /review tabs do not stop a tab that is still reviewing.
 */
const ReviewTimeTracker: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const podcastId = podcastIdFromPath(location.pathname);
  const podcastIdRef = useRef(podcastId);
  podcastIdRef.current = podcastId;

  const [shared, setShared] = useState<SharedState>(() => loadShared());
  const [weekTotalMs, setWeekTotalMs] = useState(() => loadWeekTotalMs() ?? 0);
  const [now, setNow] = useState(() => Date.now());
  const [dialogOpen, setDialogOpen] = useState(dialogOpenThisTab);

  const sharedRef = useRef(shared);
  sharedRef.current = shared;
  const dialogOpenRef = useRef(dialogOpen);
  dialogOpenRef.current = dialogOpen;
  const userPausedRef = useRef(false);

  const writeShared = useCallback((state: SharedState) => {
    saveShared(state);
    sharedRef.current = state;
    setShared(state);
  }, []);

  /**
   * Credit elapsed time since the shared lastFlushAt watermark. Re-reads
   * localStorage inside the lock so only one tab claims each interval.
   * minDeltaMs > 0 makes periodic flushes yield when another tab just flushed.
   */
  const sendHeartbeat = useCallback(
    async (deltaMs: number, activePodcastId: string | null): Promise<void> => {
      try {
        const res = await apiService.heartbeatReviewTime({
          deltaMs,
          podcastId: activePodcastId ?? undefined,
        });
        saveWeekTotalMs(res.totalMs);
        setWeekTotalMs(res.totalMs);
      } catch (err) {
        console.error("Review time heartbeat failed:", err);
      }
    },
    [],
  );

  const flush = useCallback(
    async (minDeltaMs = 0): Promise<void> => {
      await runExclusive(async () => {
        const state = loadShared();
        if (!state.running || !isOwner(state)) return;
        const flushAt = Date.now();
        const deltaMs = pendingDeltaMs(state, flushAt);
        if (deltaMs <= minDeltaMs) return;

        writeShared({ ...state, lastFlushAt: flushAt });
        await sendHeartbeat(deltaMs, state.activePodcastId);
      });
    },
    [writeShared, sendHeartbeat],
  );

  /**
   * Make this tab's podcast the one credited from now on. Pending time is
   * flushed first so it goes to the previously active podcast.
   */
  const claimActivePodcast = useCallback(async (): Promise<void> => {
    await runExclusive(async () => {
      const state = loadShared();
      if (!isOwner(state)) return;
      const next = podcastIdRef.current;
      if (state.activePodcastId === next) return;
      if (!state.running) {
        writeShared({ ...state, activePodcastId: next });
        return;
      }
      const flushAt = Date.now();
      const deltaMs = pendingDeltaMs(state, flushAt);
      writeShared({ ...state, lastFlushAt: flushAt, activePodcastId: next });
      if (deltaMs > 0) await sendHeartbeat(deltaMs, state.activePodcastId);
    });
  }, [writeShared, sendHeartbeat]);

  const startSession = useCallback(() => {
    const t = Date.now();
    setNow(t);
    writeShared({
      running: true,
      ownerTabId: TAB_ID,
      sessionStartedAt: t,
      lastFlushAt: t,
      sessionElapsedMs: 0,
      activePodcastId: podcastIdRef.current,
    });
  }, [writeShared]);

  const stopOwnedSession = useCallback(async (): Promise<void> => {
    await runExclusive(async () => {
      const state = loadShared();
      if (!state.running || !isOwner(state)) return;
      const stopAt = Date.now();
      const deltaMs = pendingDeltaMs(state, stopAt);
      writeShared({
        running: false,
        ownerTabId: null,
        sessionStartedAt: null,
        lastFlushAt: null,
        sessionElapsedMs: liveSessionMs(state, stopAt),
        activePodcastId: state.activePodcastId,
      });
      if (deltaMs > 0) await sendHeartbeat(deltaMs, state.activePodcastId);
    });
  }, [writeShared, sendHeartbeat]);

  const syncTracking = useCallback(async (): Promise<void> => {
    const should = shouldTrackReviewTime(dialogOpenRef.current, userPausedRef.current);
    const state = loadShared();
    if (should) {
      if (document.visibilityState === "visible") void claimActivePodcast();
      if (!state.running) startSession();
      return;
    }
    await stopOwnedSession();
  }, [claimActivePodcast, startSession, stopOwnedSession]);

  useEffect(() => {
    void syncTracking();
  }, [dialogOpen, podcastId, syncTracking]);

  // Cross-tab sync: storage events fire in every other tab on writes.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        const state = loadShared();
        sharedRef.current = state;
        setShared(state);
        setNow(Date.now());
        if (!state.running) void syncTracking();
      } else if (e.key === WEEK_TOTAL_KEY) {
        const total = loadWeekTotalMs();
        if (total != null) setWeekTotalMs(total);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [syncTracking]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getReviewTimeWeek();
        if (!cancelled) {
          saveWeekTotalMs(res.totalMs);
          setWeekTotalMs(res.totalMs);
        }
      } catch (err) {
        console.error("Failed to load review time week:", err);
      }
      if (!cancelled) await syncTracking();
    })();
    return () => {
      cancelled = true;
    };
  }, [syncTracking]);

  const trackingHere = shared.running && isOwner(shared);

  useEffect(() => {
    if (!trackingHere) return;
    const id = window.setInterval(() => setNow(Date.now()), TICK_MS);
    return () => window.clearInterval(id);
  }, [trackingHere]);

  useEffect(() => {
    if (!trackingHere) return;
    const id = window.setInterval(() => {
      void flush(MIN_PERIODIC_DELTA_MS);
    }, FLUSH_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [trackingHere, flush]);

  useEffect(() => {
    const onVisibility = () => {
      void syncTracking();
    };
    const onDialogOpen = (event: Event) => {
      const open = Boolean((event as CustomEvent<{ open?: boolean }>).detail?.open);
      dialogOpenRef.current = open;
      setDialogOpen(open);
      if (open) userPausedRef.current = false;
    };
    const onUnload = () => {
      const state = loadShared();
      if (!state.running || !isOwner(state)) return;
      const flushAt = Date.now();
      const deltaMs = pendingDeltaMs(state, flushAt);
      saveShared({
        ...STOPPED,
        sessionElapsedMs: liveSessionMs(state, flushAt),
        activePodcastId: state.activePodcastId,
      });
      if (deltaMs <= 0) return;
      void apiService
        .heartbeatReviewTime({ deltaMs, podcastId: state.activePodcastId ?? undefined })
        .catch(() => undefined);
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener(DIALOG_OPEN_EVENT, onDialogOpen);
    window.addEventListener("pagehide", onUnload);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener(DIALOG_OPEN_EVENT, onDialogOpen);
      window.removeEventListener("pagehide", onUnload);
      window.removeEventListener("beforeunload", onUnload);
      void stopOwnedSession();
    };
  }, [syncTracking, stopOwnedSession]);

  const handleToggle = () => {
    if (sharedRef.current.running && isOwner(sharedRef.current)) {
      userPausedRef.current = true;
      void stopOwnedSession();
      return;
    }
    if (!shouldTrackReviewTime(dialogOpenRef.current, false)) return;
    userPausedRef.current = false;
    startSession();
  };

  const sessionMs = trackingHere ? liveSessionMs(shared, now) : shared.sessionElapsedMs;
  const unflushedMs =
    trackingHere && shared.lastFlushAt != null
      ? Math.min(MAX_FLUSH_DELTA_MS, Math.max(0, now - shared.lastFlushAt))
      : 0;
  const displayWeekMs = weekTotalMs + unflushedMs;
  const label = `${trackingHere ? "⏸" : "▶"} ${formatSessionClock(sessionMs)} · week ${formatWeekHours(displayWeekMs)}`;

  return (
    <Box
      sx={{
        position: "fixed",
        top: isMobile ? 72 : 16,
        right: 16,
        zIndex: theme.zIndex.modal + 1,
        pointerEvents: "auto",
      }}
    >
      <Button
        variant="contained"
        color={trackingHere ? "success" : "inherit"}
        onClick={handleToggle}
        size="small"
        sx={{
          borderRadius: 999,
          px: 1.75,
          py: 0.75,
          minWidth: 0,
          fontVariantNumeric: "tabular-nums",
          fontWeight: 700,
          letterSpacing: 0.2,
          boxShadow: 3,
          bgcolor: trackingHere ? undefined : "background.paper",
          color: trackingHere ? undefined : "text.primary",
          border: "1px solid",
          borderColor: trackingHere ? "success.dark" : "divider",
          "&:hover": {
            bgcolor: trackingHere ? undefined : "action.hover",
          },
        }}
        aria-pressed={trackingHere}
        aria-label={trackingHere ? "Stop review timer" : "Start review timer"}
      >
        {label}
      </Button>
    </Box>
  );
};

export default ReviewTimeTracker;
